// Which reason a step records when it refuses to run because a step it reads
// is not usable.
//
// `dependency_failed` classifies `retryable`: the upstream may still be
// repaired by a retry or a replay, and the dependent must run again once it is.
// That is wrong when the upstream itself is waiting on a person. A retry then
// changes nothing, so the dependent turned a delivery that should settle into
// one that retries and dead-letters, and an operator could never settle it,
// because `migration:settle-step` only takes human-class rows.
//
// So a dependent records `dependency_unrecoverable` (registered `human`) when
// its upstream's outcome is one only a person can change, and keeps
// `dependency_failed` in every other state. One helper decides it, so every
// producer agrees on what "a person has to look" means.

import { isRegisteredHumanReason, type InvocationFailure } from './invocation-disposition';
import { isOperatorSettleReason, stepDetailCode, type MigrationStepName } from './step-manifest';

export type DependencyRefusal = 'dependency_failed' | 'dependency_unrecoverable';

/** The ledger fields the decision reads, whatever query produced them. */
export interface UpstreamLedgerRow {
  readonly status: string;
  readonly error: string | null;
}

/**
 * Whether the upstream's ledger row holds an outcome no retry will change: a
 * `failed` row with a registered human-class reason, or a `skipped` row an
 * operator settled with `migration:settle-step`.
 */
export function isUnrecoverableUpstreamRow(row: UpstreamLedgerRow | undefined): boolean {
  if (row === undefined || row.error === null) return false;
  if (row.status === 'failed') return isRegisteredHumanReason(row.error);
  if (row.status === 'skipped') return isOperatorSettleReason(stepDetailCode(row.error));
  return false;
}

/** The refusal for an upstream read from the ledger, when it is not usable. */
export function dependencyRefusalForRow(row: UpstreamLedgerRow | undefined): DependencyRefusal {
  return isUnrecoverableUpstreamRow(row) ? 'dependency_unrecoverable' : 'dependency_failed';
}

/**
 * The refusal for an upstream that failed in the same delivery. The failure
 * must have reached the ledger: an unrecorded one is retried, so its
 * dependents must be too.
 */
export function dependencyRefusalForFailure(failure: InvocationFailure): DependencyRefusal {
  return failure.ledgerRecorded !== false && isRegisteredHumanReason(failure.reason)
    ? 'dependency_unrecoverable'
    : 'dependency_failed';
}

// ─── which step waits on which ──────────────────────────────────────────────
//
// The steps that read another step's writes, and the step each reads. The
// runners refuse a dependent while its upstream is not usable, and the replay
// resolver unlocks a refused dependent once it is; both read this register and
// `isUpstreamRowUsable` below, so they cannot disagree about either half. A
// disagreement is a loop: the resolver would keep handing the runner a step
// the runner keeps refusing.

/** Every dependent step, and the step whose writes it reads. */
export const MIGRATION_STEP_DEPENDENCIES = {
  'tier1.telemedicine_appointments': 'tier1.appointments',
  'tier1.unmatched_appointments': 'tier1.appointments',
  'tier1.nrt_orders': 'tier1.addresses',
  'tier1.test_kit_orders': 'tier1.addresses',
  'tier1.baselines': 'tier0.program',
  'tier1.breathalyzer_readings': 'tier0.program',
  'tier2.gogomeds_order_items': 'tier0.prescriptions',
} as const satisfies Readonly<Partial<Record<MigrationStepName, MigrationStepName>>>;

export type DependentStepName = keyof typeof MIGRATION_STEP_DEPENDENCIES;
export type UpstreamStepName = (typeof MIGRATION_STEP_DEPENDENCIES)[DependentStepName];

function isDependentStep(step: string): step is DependentStepName {
  return Object.hasOwn(MIGRATION_STEP_DEPENDENCIES, step);
}

/** The step `step` waits on, or undefined when nothing it reads is another step's. */
export function dependencyUpstreamOf(step: string): UpstreamStepName | undefined {
  return isDependentStep(step) ? MIGRATION_STEP_DEPENDENCIES[step] : undefined;
}

/**
 * The upstreams whose `skipped` outcome still leaves their writes in place.
 *
 * The booking import settles `skipped` only after every resolvable booking is
 * written, and the programme step settles `skipped` when the member is
 * enrolled in nothing, which is a complete answer. The address import and the
 * prescriptions mirror have no `skipped` outcome of their own, so any skip
 * there is not one their dependents can trust.
 */
const SKIP_IS_USABLE: ReadonlySet<UpstreamStepName> = new Set<UpstreamStepName>([
  'tier1.appointments',
  'tier0.program',
]);

/**
 * Whether a dependent can rely on its upstream's ledger row.
 *
 * `done` always can. A `skipped` row can only for the upstreams above, and
 * never when an operator settled it: a settle means the writes were never
 * made. Every other state, an absent row included, cannot.
 */
export function isUpstreamRowUsable(
  upstream: UpstreamStepName,
  row: UpstreamLedgerRow | undefined,
): boolean {
  if (row === undefined) return false;
  if (row.status === 'done') return true;
  if (row.status !== 'skipped' || !SKIP_IS_USABLE.has(upstream)) return false;
  return !isUnrecoverableUpstreamRow(row);
}

// ─── whether a migrated member's address has landed ─────────────────────────
//
// A migrated member's home address arrives through one of two steps: Tier 0
// writes it inside the transfer, and Tier 1 re-runs the same idempotent import.
// Anything that reads the address to decide something about the member (the
// AI restriction reads the address state) must not treat "no address yet" as
// "no restricted address": until one of these steps has finished, the answer
// is not known.

/** The steps that write a migrated member's 1.0 home address. */
export const MIGRATION_ADDRESS_STEPS = [
  'tier0.addresses',
  'tier1.addresses',
] as const satisfies readonly MigrationStepName[];

/** The ledger fields the settlement reads. */
export interface MigrationStepLedgerRow {
  readonly stepName: string;
  readonly status: string;
}

export type MigrationAddressSettlement = 'not_migrating' | 'settled' | 'pending';

const ADDRESS_STEP_SET: ReadonlySet<string> = new Set<string>(MIGRATION_ADDRESS_STEPS);

/**
 * Whether a member's migrated address is in place, from their ledger rows.
 *
 * No rows at all means the member never migrated, so there is nothing to wait
 * for. Otherwise only a `done` address step settles it, whatever it wrote
 * (zero rows is a complete answer). A `skipped` row, operator-settled or not,
 * means the import never ran to the end, and every other state is still
 * pending, so each answers `pending`.
 */
export function migrationAddressSettlement(
  rows: readonly MigrationStepLedgerRow[],
): MigrationAddressSettlement {
  if (rows.length === 0) return 'not_migrating';
  return rows.some((row) => ADDRESS_STEP_SET.has(row.stepName) && row.status === 'done')
    ? 'settled'
    : 'pending';
}
